import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { ThemeProvider as MUIThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { useAuth, AuthProvider } from './context/AuthContext';
import { ThemeProvider as FTThemeProvider, useThemeMode } from './context/ThemeContext';
import { createMUITheme } from './theme/muiTheme';
import { canAccess } from './routing/navConfig';
import { useNewVersionCheck } from './hooks/useNewVersionCheck';
import NewVersionDialog from './components/NewVersionDialog';
import NewVersionSnackbar from './components/NewVersionSnackbar';
import PageLoader from './components/PageLoader';
import ErrorBoundary from './components/ErrorBoundary';
import NotificationOnboarding from './components/NotificationOnboarding';
import './App.css';

// Lazy-load pages so each route is loaded only when visited (smaller initial bundle, less risk of crash)
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const ProductionLandingPage = lazy(() => import('./pages/ProductionLandingPage'));
const ProductionPage = lazy(() => import('./pages/ProductionPage'));
const ItemPage = lazy(() => import('./pages/ItemPage'));
const ProductTypeEditLayout = lazy(() => import('./pages/productType/ProductTypeEditLayout'));
const EditHubPage = lazy(() => import('./pages/productType/EditHubPage'));
const FieldsSectionPage = lazy(() => import('./pages/productType/FieldsSectionPage'));
const StepsSectionPage = lazy(() => import('./pages/productType/StepsSectionPage'));
const QrSectionPage = lazy(() => import('./pages/productType/QrSectionPage'));
const CreateItemPage = lazy(() => import('./pages/CreateItemPage'));
const EditItemPage = lazy(() => import('./pages/EditItemPage'));
const TestBenchPage = lazy(() => import('./pages/TestBenchPage'));
const GMAOPage = lazy(() => import('./pages/GMAOPage'));
const GMAOFieldConfigPage = lazy(() => import('./pages/GMAOFieldConfigPage'));
const DashboardConfigPage = lazy(() => import('./pages/DashboardConfigPage'));
const AuditExportsPage = lazy(() => import('./pages/AuditExportsPage'));

/**
 * Route gate.
 *
 *   <PrivateRoute>
 *     <Page />
 *   </PrivateRoute>
 *
 *   <PrivateRoute requires={{ section: 'audit', action: 'view' }}>
 *     <AuditPage />
 *   </PrivateRoute>
 *
 * With `requires` set, an authenticated user without the matching
 * permission is bounced to /home instead of seeing the page render and
 * then show an inline 403 message. The per-page permission checks that
 * used to render the inline 403 can be removed once their routes get a
 * `requires` here.
 */
function PrivateRoute({ children, requires }) {
  const { isAuthenticated, loading, user, isAdmin, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />;
  }

  // Password expired: must change password before using the app
  if (user?.passwordExpired && location.pathname !== '/auth/change-password') {
    return <Navigate to="/auth/change-password" replace />;
  }

  if (!canAccess({ requires, isAdmin, hasPermission })) {
    // No permission for this route — send the user back to their landing
    // page rather than rendering the page chrome + an inline 403.
    return <Navigate to="/home" replace />;
  }

  return children;
}

/**
 * Backwards-compat redirect for the old `/production/create-type` URL.
 *  - `?edit=N`             → `/production/products/N/edit`         (hub)
 *  - `?edit=N&viewOnly=1`  → `/production/products/N/edit?viewOnly=true`
 *  - no query              → `/production/products/new`            (hub)
 */
function LegacyCreateTypeRedirect() {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const viewOnly = searchParams.get('viewOnly');
  if (editId) {
    const suffix = viewOnly ? `?viewOnly=${encodeURIComponent(viewOnly)}` : '';
    return <Navigate to={`/production/products/${editId}/edit${suffix}`} replace />;
  }
  return <Navigate to="/production/products/new" replace />;
}

/**
 * Reset scroll to the top on forward navigation (PUSH/REPLACE). React Router v6
 * does not restore or reset scroll, so without this every navigation landed at
 * the previous route's scroll offset. Back/forward (POP) is left alone so the
 * browser's native scroll restoration keeps working.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType !== 'POP') window.scrollTo(0, 0);
  }, [pathname, navType]);
  return null;
}

/** When the PWA is opened via a link (e.g. QR code scan), navigate to the target URL. */
function PwaLaunchHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof window === 'undefined' || !window.launchQueue) return;
    window.launchQueue.setConsumer((launchParams) => {
      const url = launchParams?.targetURL;
      if (url) {
        try {
          const path = new URL(url).pathname;
          if (path && path !== '/') navigate(path);
        } catch (_) { /* ignore invalid URL */ }
      }
    });
  }, [navigate]);
  return null;
}

function AppShell() {
  /* Two-stage version-update UX:
     - newVersionAvailable → soft snackbar (non-blocking, user keeps working)
     - forceDialog → 10 min later, becomes a blocking dialog (only if still ignored).
     The hook also handles SSE for near-instant detection + multi-tab sync via
     BroadcastChannel + polling fallback when SSE is blocked. */
  const { newVersionAvailable, forceDialog } = useNewVersionCheck();
  // MUI theme follows the user's resolved theme preference (light/dark) —
  // ThemeContext owns the source of truth, this just wires it to MUI.
  const { resolvedMode } = useThemeMode();
  const muiTheme = useMemo(() => createMUITheme(resolvedMode), [resolvedMode]);
  // Feeds ErrorBoundary so a route change clears a stale render error instead of
  // pinning the whole app on the fallback.
  const { pathname } = useLocation();

  return (
    <ErrorBoundary resetKey={pathname}>
      <MUIThemeProvider theme={muiTheme}>
        <CssBaseline />
        {/* AuthProvider uses useNavigate, so it must live below Router
            (Router is hoisted above ThemeProvider in App() so the theme
            context can read useLocation and force light on /, /auth*). */}
        <AuthProvider>
            <NewVersionSnackbar open={newVersionAvailable && !forceDialog} />
            <NewVersionDialog open={forceDialog} />
            <NotificationOnboarding />
            <ScrollToTop />
            <PwaLaunchHandler />
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/auth/change-password"
                element={
                  <PrivateRoute>
                    <ChangePasswordPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/home"
                element={
                  <PrivateRoute>
                    <WelcomePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute requires={{ section: 'production', action: 'view' }}>
                    <HomePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/config"
                element={
                  <PrivateRoute requires={{ section: 'production', action: 'view' }}>
                    <DashboardConfigPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/audit"
                element={
                  <PrivateRoute requires={{ section: 'audit', action: 'view' }}>
                    <AuditExportsPage />
                  </PrivateRoute>
                }
              />
              {/* Legacy URL redirect — kept so existing bookmarks still work. */}
              <Route path="/audit/exports" element={<Navigate to="/audit" replace />} />
              <Route
                path="/settings"
                element={
                  <PrivateRoute requires={{ section: 'settings', action: 'view' }}>
                    <SettingsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/:tab"
                element={
                  <PrivateRoute requires={{ section: 'settings', action: 'view' }}>
                    <SettingsPage />
                  </PrivateRoute>
                }
              />
              {/* Per-user editor — replaces the previous UserSettingsDialog
                  modal so the URL is deep-linkable, browser back works, and
                  the scroll trap of the dialog is gone. Two segments after
                  /settings, so it never collides with /settings/:tab. */}
              <Route
                path="/settings/accounts/:userId"
                element={
                  <PrivateRoute>
                    <UserSettingsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <PrivateRoute>
                    <AccountPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/production"
                element={
                  <PrivateRoute requires={{ section: 'production', action: 'view' }}>
                    <ProductionLandingPage />
                  </PrivateRoute>
                }
              />
              {/* Create / edit product type — REST-consistent with item routes.
                  Each tab is its own sub-route so URLs are bookmarkable and the
                  back/forward buttons work. The layout owns the shared form state. */}
              <Route
                path="/production/products/new"
                element={
                  <PrivateRoute requires={{ section: 'production', action: 'add' }}>
                    <ProductTypeEditLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<EditHubPage />} />
                <Route path="fields" element={<FieldsSectionPage />} />
                <Route path="steps" element={<StepsSectionPage />} />
                <Route path="qr" element={<QrSectionPage />} />
              </Route>
              <Route
                path="/production/products/:productId/edit"
                element={
                  <PrivateRoute requires={{ section: 'production', action: 'edit' }}>
                    <ProductTypeEditLayout />
                  </PrivateRoute>
                }
              >
                <Route index element={<EditHubPage />} />
                <Route path="fields" element={<FieldsSectionPage />} />
                <Route path="steps" element={<StepsSectionPage />} />
                <Route path="qr" element={<QrSectionPage />} />
              </Route>
              {/* Legacy URLs — redirect to the new scheme. */}
              <Route path="/production/create-type" element={<LegacyCreateTypeRedirect />} />
              <Route
                path="/production/products/:productId"
                element={
                  <PrivateRoute requires={{ section: 'production', action: 'view' }}>
                    <ProductionPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/production/products/:productId/items/new"
                element={
                  <PrivateRoute requires={{ section: 'production', action: 'add' }}>
                    <CreateItemPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/production/products/:productId/items/:itemId/edit"
                element={
                  <PrivateRoute requires={{ section: 'production', action: 'edit' }}>
                    <EditItemPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/production/products/:productId/items/:itemId/external"
                element={
                  <PrivateRoute requires={{ section: 'production', action: 'view' }}>
                    <TestBenchPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/production/products/:productId/items/:itemId"
                element={
                  <PrivateRoute requires={{ section: 'production', action: 'view' }}>
                    <ItemPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/gmao"
                element={
                  <PrivateRoute requires={{ section: 'gmao', action: 'view' }}>
                    <GMAOPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/gmao/config"
                element={
                  <PrivateRoute requires={{ section: 'gmao', action: 'config' }}>
                    <GMAOFieldConfigPage />
                  </PrivateRoute>
                }
              />
              </Routes>
            </Suspense>
          </AuthProvider>
      </MUIThemeProvider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <Router>
      <FTThemeProvider>
        <AppShell />
      </FTThemeProvider>
    </Router>
  );
}

export default App;

